// src/EmployeeForm.js ver 04 from ver 02 (03a) (UPDATED: Employee Role select, display fields, TenantProfileEditor logic)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './App';

import * as employeeService from './api/employeeService';
import * as userService from './api/userService';
import { ApiError } from './api/apiClient';

const initialEmployeeState = {
    AuthLoginName: '',
    FullName: '',
    EmployeeRole: 'All-around Administrator', // Default role
    EmployeeStatus: 'Active-On Probation', // Default status
    Type: 'Intern', // Default type
};

// Allowed dropdown options (for frontend consistency)
const employeeRoles = ['All-around Administrator', 'HR Manager', 'Procurement Manager'];
const employeeStatuses = ['Active-On Probation', 'Active-Regular', 'Active-OnExtendedLeave', 'Active-Suspended', 'Terminated'];
const employeeTypes = ['Intern', 'Casual', 'Regular', 'Consultant'];


function EmployeeForm() {
    // const { tenantId, id: employeeId } = useParams(); // ver 00a-debug
    const { id: employeeId } = useParams(); // ver 04 left employeeId // ver 00a-debug
    const navigate = useNavigate();
    const isEditing = Boolean(employeeId);
    const auth = useAuth();

    const [formData, setFormData] = useState(initialEmployeeState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authLoginNameError, setAuthLoginNameError] = useState(null);
    const [associatedUserName, setAssociatedUserName] = useState(null);
    const [isAuthLoginNameValid, setIsAuthLoginNameValid] = useState(false);

    const tenantId = auth.userProfile?.SubsCoDocId; // re-added ver01. existing in ver 00a

    const validateAuthLoginName = useCallback(async (email, token) => {
        setAssociatedUserName(null);
        setAuthLoginNameError(null);
        setIsAuthLoginNameValid(false);

        if (!email) return;

        try {
            const userProfile = await userService.getUserProfile(email, token);
            if (userProfile && userProfile.id) {
                const name = `${userProfile.LastName || ''}, ${userProfile.FirstName || ''} ${userProfile.MiddleName || ''}`.trim();
                setAssociatedUserName(name || `(Profile found, no name set)`);
                setIsAuthLoginNameValid(true);
            } else {
                setAuthLoginNameError("The Login email being added lacks a user profile.");
                return;
            }
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
                setAuthLoginNameError("The Login email being added lacks a user profile.");
            } else {
                console.error("Error fetching user profile for AuthLoginName validation:", err);
                setAuthLoginNameError(`Error checking user profile: ${err.message}`);
            }
            return;
        }

        setIsAuthLoginNameValid(true);
    }, [userService]);


    useEffect(() => {
        const fetchData = async () => {
            if (!tenantId) {
                setError("Tenant ID missing in URL.");
                setLoading(false);
                return;
            }

            if (isEditing) {
                setLoading(true);
                setError(null);
                try {
                    const token = await auth.getIdToken();
                    if (!token) {
                        setError("Authentication required. Please log in.");
                        setLoading(false);
                        return;
                    }
                    const data = await employeeService.getEmployeeById(tenantId, employeeId, token);
                    if (data) {
                        setFormData(data);
                        setIsAuthLoginNameValid(true); // Assumed valid if record exists
                        await validateAuthLoginName(data.AuthLoginName, token);
                    } else {
                        setError("Employee not found.");
                        navigate(`/employees/tenant/${tenantId}`);
                    }
                } catch (err) {
                    console.error("Error fetching employee from Cloud Function: ", err);
                    setError(`Failed to load employee details: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, employeeId, isEditing, navigate, auth, validateAuthLoginName]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAuthLoginNameBlur = async (e) => {
        if (!isEditing) {
            const token = auth.user ? await auth.getIdToken() : null;
            if (token) {
                await validateAuthLoginName(e.target.value, token);
            } else {
                setAuthLoginNameError("No authenticated user to perform email validation.");
                setIsAuthLoginNameValid(false);
            }
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setAuthLoginNameError(null);

        if (!tenantId) {
            setError("Tenant ID missing. Cannot save employee.");
            return;
        }

        if (!isEditing && !isAuthLoginNameValid) {
            setAuthLoginNameError("Cannot save: The Login email lacks a valid user profile.");
            const token = auth.user ? await auth.getIdToken() : null;
            if (token) await validateAuthLoginName(formData.AuthLoginName, token);
            return;
        }
        
        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication required. Please log in.");
                return;
            }

            const payload = { ...formData };

            if (isEditing) {
                await employeeService.updateEmployee(tenantId, employeeId, payload, token);
                alert("Employee updated successfully!");
                auth.refreshUserProfile(); // Refresh the HR manager's profile to see if their own tenant access changes
            } else {
                await employeeService.addEmployee(tenantId, payload, token);
                alert("Employee created successfully and user profile updated!");
                auth.refreshUserProfile(); // Refresh the HR manager's profile to see if their own tenant access changes
            }
            // navigate(`/employees/tenant/${tenantId}`);
            navigate(`/employees`);
        } catch (err) {
            console.error("Error saving employee via Cloud Function:", err);
            if (err instanceof ApiError && err.status === 409) {
                setAuthLoginNameError(`Error: ${err.message}`);
                setError(null);
            } else {
                setError(`Failed to save employee: ${err.message}`);
            }
        }
    };

    if (loading) return <p>Loading employee details...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p className="container" style={{ color: 'red' }}>Error: Tenant ID is required to add/edit employee.</p>;


    return (
        <div className="container form-container">
            <h2>{isEditing ? `Edit Employee (Tenant: ${tenantId})` : `Add New Employee (Tenant: ${tenantId})`}</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Auth Login Name (Email)</label>
                    <input
                        type="email"
                        name="AuthLoginName"
                        value={formData.AuthLoginName}
                        onChange={handleChange}
                        onBlur={handleAuthLoginNameBlur}
                        required
                        disabled={isEditing}
                    />
                    {authLoginNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{authLoginNameError}</p>}
                </div>

                {associatedUserName && (
                    <div className="form-group" style={{ marginTop: '-0.5rem', marginBottom: '0.5rem', fontSize: '0.9em', color: '#555' }}>
                        Associated User: <strong>{associatedUserName}</strong>
                    </div>
                )}
                {isEditing && (
                    <div className="form-group">
                        <label>Employee ID</label>
                        <input type="text" name="EmployeeId" value={formData.EmployeeId || ''} disabled />
                    </div>
                )}

                <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" name="FullName" value={formData.FullName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                    <label>Employee Role</label>
                    <select name="EmployeeRole" value={formData.EmployeeRole} onChange={handleChange} required>
                        {employeeRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Employee Status</label>
                    <select name="EmployeeStatus" value={formData.EmployeeStatus} onChange={handleChange} required>
                        {employeeStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Type</label>
                    <select name="Type" value={formData.Type} onChange={handleChange} required>
                        {employeeTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>
                {isEditing && formData.DateAdded && (
                    <div className="form-group">
                        <label>Date Added</label>
                        <input type="text" value={new Date(formData.DateAdded._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}
                {isEditing && formData.DateLastModified && (
                    <div className="form-group">
                        <label>Date Last Modified</label>
                        <input type="text" value={new Date(formData.DateLastModified._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}

                <button type="submit">Save Employee</button>
            </form>
        </div>
    );
}

export default EmployeeForm;